import { Avatar, AvatarGroup, Box, Button, Chip, Skeleton, Stack, styled, Typography } from "@mui/material";
import { ITouchpoint, ViewpointStatus } from "../../../../types";
import { DisplayBoxBase, TagChip } from "../../../../components/shared/StyledComponents";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useAdvisor } from "../../context/AdvisorContext";
import { VideoJS } from "../../../../components/VideoJs";
import useAnalytics from "../../../../hooks/useAnalytics";
import { useDealContext } from "../../../deal";


const TouchpointHeader = styled(Typography)(({ theme }) => ({
    fontSize: "1rem",
    fontWeight: "bold",
    lineHeight: "1"
}));

const TouchpointDate = styled(Typography)(({ theme }) => ({
    lineHeight: "1"
}));

export const AdvisorInsightsItem = ({ touchpoint }: { touchpoint: ITouchpoint }) => {
    const { advisor } = useAdvisor();
    const { trackAdvisorEvent } = useAnalytics();
    const { dealContext } = useDealContext();

    const handleVideoPlay = (videoUrl: string) => {
        trackAdvisorEvent(advisor, "Watched advisor highlight video", {
            project: dealContext?.project.tag,
            organization: dealContext?.project?.organization?.tag,
            touchpoint_uuid: touchpoint.uuid,
            touchpoint_name: touchpoint.name,
            video_url: videoUrl,
        });
    }

    if (!touchpoint.viewpoints?.some((viewpoint) => viewpoint.advisor?.uuid === advisor?.uuid && viewpoint?.status && [ViewpointStatus.COMPLETED, ViewpointStatus.APPROVED].includes(viewpoint.status))) {
        const inProgress = touchpoint.viewpoints?.some((viewpoint) => viewpoint.advisor?.uuid === advisor?.uuid && viewpoint?.status && viewpoint.status === ViewpointStatus.IN_PROGRESS);
        return (
            <DisplayBoxBase
                className="highlighted"
                sx={{
                    gap: 2,
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    display: "flex",
                }}
            >
                <Box sx={{ flex: "1 1 0" }}>
                    <TouchpointHeader><Link to={`/advisor/feedback/${touchpoint.uuid}`}>{touchpoint.name}</Link></TouchpointHeader>
                    <Typography variant="body1" >
                        Review the question and included materials, then record your response. Once you're done you'll be able to see highlights from other advisors and a summary of responses.
                    </Typography>
                    <Box display="flex" alignItems="center" sx={{ padding: "0.2rem 0" }}>
                        <AvatarGroup max={6}>
                            {touchpoint.viewpoints?.slice(0, 6).map((viewpoint) => (
                                <Avatar
                                    key={`${viewpoint.advisor?.uuid}-${viewpoint.id}`}
                                    src={viewpoint.advisor?.user?.profile?.image_url}
                                    sx={{ width: 24, height: 24 }}
                                />
                            ))}
                            {!touchpoint.viewpoints?.length && (
                                <Skeleton variant="circular" width={24} height={24} />
                            )}
                        </AvatarGroup>
                        {touchpoint.viewpoints && touchpoint.viewpoints.length > 6 && (
                            <Typography sx={{ ml: 1, fontWeight: 600, fontSize: "0.8rem" }}>
                                +{touchpoint.viewpoints.length - 6} advisors
                            </Typography>
                        )}
                        {touchpoint.viewpoints && touchpoint.viewpoints.length < 6 && (
                            <Typography sx={{ ml: 1, fontWeight: 600, fontSize: "0.8rem" }}>
                                Collecting responses...
                            </Typography>
                        )}
                    </Box>


                </Box>
                <Button component={Link} to={`/advisor/feedback/${touchpoint.uuid}`} variant="contained" color="primary">{inProgress ? "Continue" : "Start"}</Button>
            </DisplayBoxBase>
        );
    }

    return (
        <DisplayBoxBase
            className={touchpoint.status === "active" ? "gray" : "white"}
            sx={{
                gap: 2,
                justifyContent: "space-between",
                width: "100%",
                display: "flex",
                alignItems: "stretch",
                flexWrap: "wrap",
            }}
        >
            <Stack direction="column" gap={1} sx={{ flex: 1 }} justifyContent="space-between">
                {touchpoint.status !== "active" &&
                    <Box>
                        <TouchpointDate variant="caption">
                            {dayjs(touchpoint.started_at).format("MMM DD, YYYY")}{touchpoint.completed_at ? ` - ${dayjs(touchpoint.completed_at).format("MMM DD, YYYY")}` : ""}
                        </TouchpointDate>
                    </Box>
                }
                <TouchpointHeader><Link to={`/advisor/feedback/${touchpoint.uuid}`}>{touchpoint.name}</Link></TouchpointHeader>
                {/* Custom avatar row with overflow label */}
                <Box display="flex" alignItems="center" sx={{ padding: "0.2rem 0" }}>
                    <AvatarGroup max={6}>
                        {touchpoint.viewpoints?.slice(0, 6).map((viewpoint) => (
                            <Avatar
                                key={`${viewpoint.advisor?.uuid}-${viewpoint.id}`}
                                src={viewpoint.advisor?.user?.profile?.image_url}
                                sx={{ width: 24, height: 24 }}
                            />
                        ))}
                        {!touchpoint.viewpoints?.length && (
                            <Skeleton variant="circular" width={24} height={24} />
                        )}
                    </AvatarGroup>
                    {touchpoint.viewpoints && touchpoint.viewpoints.length > 6 && (
                        <Typography sx={{ ml: 1, fontWeight: 600, fontSize: "0.8rem" }}>
                            +{touchpoint.viewpoints.length - 6} advisors
                        </Typography>
                    )}
                    {touchpoint.viewpoints?.length && touchpoint.viewpoints.length < 6 && (
                        <Typography sx={{ ml: 1, fontWeight: 600, fontSize: "0.8rem" }}>
                            Collecting responses...
                        </Typography>
                    )}
                </Box>
                {touchpoint.status === "active" && touchpoint.media_analysis_summary && (
                    <Box
                        dangerouslySetInnerHTML={{
                            __html: touchpoint.media_analysis_summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }}
                    />
                )}
                {touchpoint.status === "active" && !touchpoint.media_analysis_summary && (
                    <Box>
                        <Typography variant="body1" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                            Check back soon for a summary of the feedback and a glimpse of what advisors had to say.
                        </Typography>
                    </Box>
                )}
                <Stack direction="row" gap={1} sx={{ padding: "0.2rem 0", mt: 'auto' }}>
                    {touchpoint.media_analysis_topics && touchpoint.media_analysis_topics.slice(0, 3).map((topic) => (
                        <TagChip key={topic} variant="outlined" label={topic} />
                    ))}
                </Stack>
            </Stack>
            {touchpoint.status === "active" && touchpoint.advisor_highlight_video_url ? (
                <Box sx={{ height: "100%", width: "416px" }}>
                    {touchpoint.advisor_highlight_video_url ? (
                        <VideoJS
                            src={touchpoint.advisor_highlight_video_url}
                            startAt={0.5}
                            onPlay={() => handleVideoPlay(touchpoint.advisor_highlight_video_url || "")}
                            size="small"
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 416,
                                height: 234,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "grey.100",
                                borderRadius: 1,
                                border: "1px dashed",
                                borderColor: "grey.300"
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Highlight reel coming soon
                            </Typography>
                        </Box>
                    )}
                </Box>
            ) : (
                <Box sx={{ height: "100%", width: "208px" }}>
                    {touchpoint.advisor_highlight_video_url ? (
                        <VideoJS
                            src={touchpoint.advisor_highlight_video_url}
                            startAt={0.5}
                            onPlay={() => handleVideoPlay(touchpoint.advisor_highlight_video_url || "")}
                            size="small"
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 208,
                                height: 117,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "grey.100",
                                borderRadius: 1,
                                border: "1px dashed",
                                borderColor: "grey.300"
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" textAlign="center">
                                Highlight reel coming soon
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </DisplayBoxBase>
    );
};